import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import persistReducer from "redux-persist/es/persistReducer";
import storage from "redux-persist/lib/storage";
import {
  CONFIG_CREATE_URL,
  CONFIG_DELETE_URL,
  CONFIG_LIST_URL,
  CONFIG_SECTION_URL,
  CONFIG_UPDATE_URL,
} from "../../Services/endpoints";
import { errorHandler, successToast } from "../../Utils/toast";
import { _delete, _get, _patch, _post } from "../../Services/baseApi";
import {
  DEFAULT_WL_THRESHOLDS,
  resolveWlThresholds,
  setRuntimeWlThresholds,
} from "../../Utils/telemetryThresholds";

export const CONFIG_SECTION_WATERLEVEL_ALERTS = "waterlevel-alerts";

function parseWlThresholdsFromRows(rows = []) {
  const byKey = Object.fromEntries(
    (rows ?? []).map((row) => [row.key, row.value]),
  );
  return resolveWlThresholds({
    belowCm: Number(byKey.below_trigger_cm),
    aboveCm: Number(byKey.above_trigger_cm),
  });
}

const initialValue = {
  isLoading: false,
  configs: [],
  total: 0,
  page: 1,
  limit: 10,
  wlThresholds: { ...DEFAULT_WL_THRESHOLDS },
};

const configSlice = createSlice({
  name: "config",
  initialState: initialValue,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(createConfigThunk.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(createConfigThunk.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(createConfigThunk.rejected, (state) => {
      state.isLoading = false;
    });

    builder.addCase(getConfigsThunk.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getConfigsThunk.fulfilled, (state, action) => {
      state.configs = action?.payload?.result?.data || [];
      state.total = action?.payload?.result?.meta?.total || 0;
      state.page = action?.payload?.result?.meta?.page;
      state.limit = action?.payload?.result?.meta?.limit;
      state.isLoading = false;
    });
    builder.addCase(getConfigsThunk.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addCase(getWaterLevelThresholdsThunk.fulfilled, (state, action) => {
      const next = action.payload ?? DEFAULT_WL_THRESHOLDS;
      state.wlThresholds = next;
      setRuntimeWlThresholds(next);
    });

    builder.addCase(updateConfigThunk.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(updateConfigThunk.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(updateConfigThunk.rejected, (state) => {
      state.isLoading = false;
    });

    builder.addCase(deleteConfigThunk.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(deleteConfigThunk.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(deleteConfigThunk.rejected, (state) => {
      state.isLoading = false;
    });
  },
});

export default persistReducer({ key: "config", storage }, configSlice.reducer);

export const createConfigThunk = createAsyncThunk(
  "configSlice/createConfigThunk",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await _post(CONFIG_CREATE_URL, {
        section: payload?.section || "alert-receiver",
        key: payload?.key || undefined,
        label: payload?.label,
        value: payload?.value,
        sortOrder: payload?.sortOrder,
        smsEnabled: payload?.smsEnabled,
        whatsappEnabled: payload?.whatsappEnabled,
      });
      if (response?.data) {
        successToast(response.data.message || "Config created successfully");
        payload?.handleFun?.();
        return response.data;
      }
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getConfigsThunk = createAsyncThunk(
  "configSlice/getConfigsThunk",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await _get(CONFIG_LIST_URL, {
        page: payload?.page || undefined,
        limit: payload?.limit || undefined,
        searchText: payload?.searchText || undefined,
        sortKey: payload?.sortColumn || undefined,
        sortOrder: payload?.sortDirection || undefined,
        section: payload?.section || "alert-receiver",
      });
      if (response?.data) {
        return response.data;
      }
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getWaterLevelThresholdsThunk = createAsyncThunk(
  "configSlice/getWaterLevelThresholdsThunk",
  async (_, { rejectWithValue }) => {
    try {
      const response = await _get(
        `${CONFIG_SECTION_URL}/${CONFIG_SECTION_WATERLEVEL_ALERTS}`,
      );
      const rows = response?.data?.result;
      const list = Array.isArray(rows) ? rows : rows?.data || [];
      return parseWlThresholdsFromRows(list);
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const updateConfigThunk = createAsyncThunk(
  "configSlice/updateConfigThunk",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await _patch(CONFIG_UPDATE_URL, {
        id: payload?.id,
        section: payload?.section || undefined,
        key: payload?.key || undefined,
        label: payload?.label || undefined,
        value: payload?.value || undefined,
        sortOrder: payload?.sortOrder,
        smsEnabled: payload?.smsEnabled,
        whatsappEnabled: payload?.whatsappEnabled,
      });
      if (response?.data) {
        if (!payload?.silent) {
          successToast(response.data.message || "Config updated successfully");
        }
        payload?.handleFun?.();
        return response.data.result;
      }
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const deleteConfigThunk = createAsyncThunk(
  "configSlice/deleteConfigThunk",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await _delete(CONFIG_DELETE_URL + payload?.id);
      if (response?.data) {
        successToast(response.data.message || "Config deleted successfully");
        payload?.handleFun?.();
        return payload?.id;
      }
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);
